import type { AddressSearchResult } from '../../../lib/addressApi';
import { api, apiBlob, apiUpload } from '../../../lib/api';
import type { PagedResult } from '../../administration/users/userTypes';
import type { OrgUnitOption } from '../../human-resources/organization/organizationTypes';
import type { SurveyAnswerSource } from '../../learning-development/survey/components/SurveyAnswerModal';
import type {
  MySurveyResponse,
  SubmitSurveyResponseRequest,
  SurveyQuestionnaireQuestion,
  SurveyResponseState,
} from '../../learning-development/survey/surveyTypes';
import type {
  CompleteMyTrainingRequest,
  EndMyRecommendationTrainingRequest,
  MySurvey,
  MySurveyAccount,
  MySurveyAnswers,
  MySurveyDevelopmentPlan,
  MySurveyQuestionnaire,
  MySurveyTraining,
  MySurveyTrainingRecommendation,
  MySurveyTrainingRecommendationRow,
  SaveMyRecommendationEnrollmentRequest,
  SaveMyRecommendationTrainingRequest,
  SaveMyTrainingReportRequest,
  SaveMyTrainingRequest,
} from './mySurveyTypes';

/** What the list may be narrowed by. */
export interface MySurveyListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  /** The SURVEY's own live-or-deleted state — the shell's key. */
  status?: string;
  /** `MySurveyStage` names. Named `stage` because `status` is the shell's. */
  stage?: string;
}

function toQueryString(query: MySurveyListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  // Sent whenever PRESENT, empty string included — "none of these" matches no row.
  if (query.status !== undefined) params.set('status', query.status);
  if (query.stage !== undefined) params.set('stage', query.stage);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

const BASE = '/api/self-service/surveys';

/**
 * The surveys that cover the signed-in employee.
 *
 * **THERE IS NO SCOPE OR RESPONDENT PARAMETER, and that is not an omission.** Which surveys come
 * back, and whose answers, is fixed by the ROUTE server-side; a survey outside it is a 404.
 */
/**
 * The address catalog, for the Start training form's Location picker — the form's OWN feed, on
 * `my-surveys.training`. The catalog's general search needs `personnel.read`, which an employee
 * does not hold. A module-level function, so `<AddressPicker search>` gets a stable reference.
 */
export function searchMyTrainingAddresses(query: string, signal?: AbortSignal): Promise<AddressSearchResult[]> {
  return api<AddressSearchResult[]>(`${BASE}/addresses/search?q=${encodeURIComponent(query)}&take=20`, { signal });
}

export const mySurveysApi = {
  list: (query: MySurveyListQuery, signal?: AbortSignal) =>
    api<PagedResult<MySurvey>>(`${BASE}${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<MySurvey>(`${BASE}/${id}`, { signal }),

  /** The caller's own answers in words — the profile's Preview. 404 while they have not answered. */
  myAnswers: (id: string, signal?: AbortSignal) => api<MySurveyAnswers>(`${BASE}/${id}/my-answers`, { signal }),

  /** The approved development plan and the caller's answer to it — 404 before approval. */
  developmentPlan: (id: string, signal?: AbortSignal) =>
    api<MySurveyDevelopmentPlan>(`${BASE}/${id}/development-plan`, { signal }),

  /** Accepts the approved plan — `my-surveys.decide-plan`. Final. */
  acceptPlan: (id: string) => api<MySurveyDevelopmentPlan>(`${BASE}/${id}/development-plan/accept`, { method: 'POST' }),

  /** Declines it with a REQUIRED reason — `my-surveys.decide-plan`. Final; it reopens for L&D. */
  declinePlan: (id: string, reason: string) =>
    api<MySurveyDevelopmentPlan>(`${BASE}/${id}/development-plan/decline`, { method: 'POST', body: { reason } }),

  /** Starts — or corrects — the training on the ACCEPTED plan — `my-surveys.training`. One per plan. */
  saveTraining: (id: string, request: SaveMyTrainingRequest) =>
    api<MySurveyTraining>(`${BASE}/${id}/development-plan/training`, { method: 'PUT', body: request }),

  /** Marks the training completed, with remarks — `my-surveys.training`. 409 before its end date; final. */
  completeTraining: (id: string, request: CompleteMyTrainingRequest) =>
    api<MySurveyTraining>(`${BASE}/${id}/development-plan/training/complete`, { method: 'POST', body: request }),

  /** Submits — or revises — the report on the COMPLETED training — `my-surveys.training`. Sanitised on write. */
  saveTrainingReport: (id: string, request: SaveMyTrainingReportRequest) =>
    api<MySurveyTraining>(`${BASE}/${id}/development-plan/training/report`, { method: 'PUT', body: request }),

  /** Files ONE MORE document against the report — PDF, PNG or JPEG at 10 MB; a report carries up to ten. */
  attachTrainingReport: (id: string, file: File, onProgress?: (percent: number) => void) => {
    const form = new FormData();
    form.append('file', file);

    return apiUpload<MySurveyTraining>(`${BASE}/${id}/development-plan/training/report/attachments`, form, { onProgress });
  },

  /** Removes ONE of the report's files. The report and its other files stay. */
  removeTrainingReportAttachment: (id: string, attachmentId: string) =>
    api<MySurveyTraining>(`${BASE}/${id}/development-plan/training/report/attachments/${attachmentId}`, { method: 'DELETE' }),

  /**
   * Fetches ONE of the caller's OWN attached files. A FETCH then an object URL, never an `<a href>`
   * at the endpoint: it carries a bearer token no link sends, and is served as an attachment.
   */
  downloadTrainingReportAttachment: (id: string, attachmentId: string) =>
    apiBlob(`${BASE}/${id}/development-plan/training/report/attachments/${attachmentId}`),

  // --- The caller's track on a SENT training recommendation: decide, train, report ---

  /** Accepts a sent recommendation — `my-surveys.decide-plan`. Final. */
  acceptRecommendation: (id: string, recommendationId: string) =>
    api<MySurveyTrainingRecommendation>(`${BASE}/${id}/training-recommendations/${recommendationId}/accept`, { method: 'POST' }),

  /** Declines it, with a required reason — `my-surveys.decide-plan`. Final. */
  declineRecommendation: (id: string, recommendationId: string, reason: string) =>
    api<MySurveyTrainingRecommendation>(`${BASE}/${id}/training-recommendations/${recommendationId}/decline`, {
      method: 'POST',
      body: { reason },
    }),

  /** Starts — or corrects — the training on an ACCEPTED recommendation — `my-surveys.training`. */
  /** Enrolls the caller in an accepted recommendation's training — the school and its location — or corrects that. */
  saveRecommendationEnrollment: (id: string, recommendationId: string, request: SaveMyRecommendationEnrollmentRequest) =>
    api<MySurveyTrainingRecommendation>(`${BASE}/${id}/training-recommendations/${recommendationId}/enrollment`, {
      method: 'PUT',
      body: request,
    }),

  saveRecommendationTraining: (id: string, recommendationId: string, request: SaveMyRecommendationTrainingRequest) =>
    api<MySurveyTrainingRecommendation>(`${BASE}/${id}/training-recommendations/${recommendationId}/training`, {
      method: 'PUT',
      body: request,
    }),

  /** Marks that training completed, with remarks. 409 before its end date; final. */
  completeRecommendationTraining: (id: string, recommendationId: string, request: EndMyRecommendationTrainingRequest) =>
    api<MySurveyTrainingRecommendation>(`${BASE}/${id}/training-recommendations/${recommendationId}/training/complete`, {
      method: 'POST',
      body: request,
    }),

  /** Submits — or revises — the report on that COMPLETED training. Sanitised on write. */
  saveRecommendationReport: (id: string, recommendationId: string, request: SaveMyTrainingReportRequest) =>
    api<MySurveyTrainingRecommendation>(`${BASE}/${id}/training-recommendations/${recommendationId}/training/report`, {
      method: 'PUT',
      body: request,
    }),

  /** Files one more document against that report. */
  attachRecommendationReport: (
    id: string,
    recommendationId: string,
    file: File,
    onProgress?: (percent: number) => void,
    /** Attached through the form's Certificates field rather than its other Attachments. */
    certificate = false,
  ) => {
    const form = new FormData();
    form.append('file', file);
    form.append('certificate', String(certificate));

    return apiUpload<MySurveyTrainingRecommendation>(
      `${BASE}/${id}/training-recommendations/${recommendationId}/training/report/attachments`,
      form,
      { onProgress },
    );
  },

  removeRecommendationReportAttachment: (id: string, recommendationId: string, attachmentId: string) =>
    api<MySurveyTrainingRecommendation>(
      `${BASE}/${id}/training-recommendations/${recommendationId}/training/report/attachments/${attachmentId}`,
      { method: 'DELETE' },
    ),

  /** FETCHES one of the caller's own files on that report — never a link at the endpoint. */
  downloadRecommendationReportAttachment: (id: string, recommendationId: string, attachmentId: string) =>
    apiBlob(`${BASE}/${id}/training-recommendations/${recommendationId}/training/report/attachments/${attachmentId}`),

  /** The APPROVED training recommendations on the plan the caller belongs to — empty when there are none. */
  trainingRecommendations: (id: string, signal?: AbortSignal) =>
    api<MySurveyTrainingRecommendation[]>(`${BASE}/${id}/training-recommendations`, { signal }),

  /** The recommendations as a TABLE — searched (title), sorted and paged by the server. */
  trainingRecommendationsTable: (
    id: string,
    query: { search?: string; page?: number; pageSize?: number; sort?: string },
    signal?: AbortSignal,
  ) => api<PagedResult<MySurveyTrainingRecommendationRow>>(`${BASE}/${id}/training-recommendations/table${toQueryString(query)}`, { signal }),

  /** ONE recommendation sent to the caller, with their whole track — its profile. 404 when it is not theirs. */
  trainingRecommendation: (id: string, recommendationId: string, signal?: AbortSignal) =>
    api<MySurveyTrainingRecommendation>(`${BASE}/${id}/training-recommendations/${recommendationId}`, { signal }),

  /** 200 either way; `error` says why this account cannot answer. */
  me: (signal?: AbortSignal) => api<MySurveyAccount>(`${BASE}/me`, { signal }),

  questionnaire: (surveyId: string, id: string, signal?: AbortSignal) =>
    api<{ questionnaire: MySurveyQuestionnaire; questions: SurveyQuestionnaireQuestion[] }>(`${BASE}/${surveyId}/questionnaires/${id}`, { signal }),

  myResponse: (surveyId: string, id: string, signal?: AbortSignal) =>
    api<SurveyResponseState>(`${BASE}/${surveyId}/questionnaires/${id}/response`, { signal }),

  /** As the caller, always — `my-surveys.respond`. A respondent on the body is ignored. */
  respond: (surveyId: string, id: string, request: SubmitSurveyResponseRequest) =>
    api<MySurveyResponse>(`${BASE}/${surveyId}/questionnaires/${id}/response`, {
      method: 'POST',
      body: { ...request, respondentPersonnelId: null },
    }),

  /** The chart for a Division question — `my-surveys.respond`, in place of `org-units.read`. */
  orgUnits: (signal?: AbortSignal) => api<OrgUnitOption[]>(`${BASE}/org-units`, { signal }),
};

/** The shared answer form, pointed at this surface and its keys. Never answers on behalf. */
export const MY_SURVEY_ANSWER_SOURCE: SurveyAnswerSource = {
  get: mySurveysApi.questionnaire,
  myResponse: mySurveysApi.myResponse,
  respond: mySurveysApi.respond,
  units: mySurveysApi.orgUnits,
  respondPermission: 'my-surveys.respond',
  onBehalfPermission: null,
};
